import { createContext, useContext, useEffect, useState } from 'react';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, UtensilsCrossed, Receipt, LogOut, ChefHat, Tag, Settings, Mail, Users, Star, Wallet } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const AdminAuthContext = createContext(null);
export const useAdminAuth = () => useContext(AdminAuthContext);

export const AdminAuthProvider = ({ children }) => {
  const [password, setPassword] = useState(() => sessionStorage.getItem('pushpanjali_admin') || '');
  const login = async (pwd) => {
    try {
      await api.post('/admin/login', { password: pwd });
      sessionStorage.setItem('pushpanjali_admin', pwd);
      setPassword(pwd);
      return true;
    } catch { return false; }
  };
  const logout = () => { sessionStorage.removeItem('pushpanjali_admin'); setPassword(''); };
  return <AdminAuthContext.Provider value={{ password, login, logout }}>{children}</AdminAuthContext.Provider>;
};

export function AdminLogin() {
  const { password, login } = useAdminAuth();
  const [pwd, setPwd] = useState('');
  const [loading, setLoading] = useState(false);
  if (password) return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(pwd);
    if (!ok) toast.error('Invalid password');
    setLoading(false);
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 pb-24 md:pb-0">
      <div className="max-w-sm w-full bg-white rounded-3xl p-8 shadow-warm border border-borderbeige">
        <div className="w-14 h-14 rounded-2xl bg-maroon flex items-center justify-center mx-auto mb-4">
          <img src="/pushpanjaliLogoMain.png" alt="Logo" className="w-full h-full object-contain" />
          {/* <ChefHat className="w-7 h-7 text-golden" /> */}
        </div>
        <h1 className="font-heading font-extrabold text-2xl text-center text-textprimary mb-2">Admin Login</h1>
        <p className="text-center text-textsecondary font-body text-sm mb-6">Enter your admin password to continue</p>
        <form onSubmit={submit}>
          <input
            data-testid="admin-password-input"
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="Password"
            className="w-full px-4 py-3 border border-borderbeige rounded-xl font-body focus:outline-none focus:ring-2 focus:ring-saffron/40"
          />
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="mt-4 w-full bg-saffron text-white py-3 rounded-2xl font-heading font-bold shadow-warm hover:bg-saffron-dark disabled:opacity-60"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

export function AdminLayout() {
  const { password, logout } = useAdminAuth();
  const location = useLocation();
  if (!password) return <Navigate to="/admin" replace />;

  const links = [
    { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'admin-nav-dashboard' },
    { to: '/admin/menu', label: 'Menu', icon: UtensilsCrossed, testId: 'admin-nav-menu' },
    { to: '/admin/orders', label: 'Orders', icon: Receipt, testId: 'admin-nav-orders' },
    { to: '/admin/payments', label: 'Payments', icon: Wallet, testId: 'admin-nav-payments' },
    { to: '/admin/users', label: 'People', icon: Users, testId: 'admin-nav-users' },
    { to: '/admin/coupons', label: 'Coupons', icon: Tag, testId: 'admin-nav-coupons' },
    { to: '/admin/contact', label: 'Contact Us', icon: Mail, testId: 'admin-nav-contact' },
    { to: '/admin/settings', label: 'Settings', icon: Settings, testId: 'admin-nav-settings' },
    { to: '/admin/reviews', label: 'Reviews', icon: Star, testId: 'admin-nav-reviews' },
  ];
  return (
    <div className="flex min-h-screen bg-bgmain pb-20 md:pb-0">
      <aside className="hidden md:flex flex-col w-60 bg-maroon text-white p-5 sticky top-0 h-screen">
        <div className="flex items-center gap-2 mb-8">
          <ChefHat className="w-6 h-6 text-golden" />
          <div>
            <div className="font-heading font-bold">Pushpanjali</div>
            <div className="text-golden text-xs">Admin Panel</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1">
          {links.map((l) => {
            const active = location.pathname === l.to;
            const Icon = l.icon;
            return (
              <Link key={l.to} to={l.to} data-testid={l.testId} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-heading font-medium text-sm transition-colors ${active ? 'bg-white/10 text-golden' : 'text-white/80 hover:bg-white/5'}`}>
                <Icon className="w-4 h-4" />
                {l.label}
              </Link>
            );
          })}
        </nav>
        <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-white/70 hover:text-white text-sm font-body py-2">
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </aside>

      <div className="md:hidden fixed top-0 left-0 right-0 bg-maroon z-40 px-4 py-3 flex justify-between items-center">
        <span className="text-white font-heading font-bold">Pushpanjali Admin</span>
        <button onClick={logout} className="text-white/80 text-sm" data-testid="admin-logout-mobile">Logout</button>
      </div>

      <main className="flex-1 md:p-8 p-4 pt-16 md:pt-8 overflow-x-hidden">
        <Outlet />
      </main>

      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-borderbeige z-40">
        <div className="grid grid-cols-5">
          {links.map((l) => {
            const active = location.pathname === l.to;
            const Icon = l.icon;
            return (
              <Link key={l.to} to={l.to} className={`flex flex-col items-center py-2 ${active ? 'text-saffron' : 'text-textsecondary'}`}>
                <Icon className="w-5 h-5" />
                <span className="text-[10px] mt-1">{l.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
